import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer.tsx';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button.tsx';
import { setAddToProjectItem, useUiStore } from '@/stores/ui.store.ts';
import { CheckIcon, PlusIcon, XIcon } from 'lucide-react';
import { ProjectSearchInput } from '@/app/products/add-to-project/project-search-input.tsx';
import {
  useAddToProjectMutation,
  useProjectCreateMutation,
  useProjectSearchQuery,
  useRemoveFromProjectMutation,
} from '@/app/products/api/queries.ts';
import { ScrollArea } from '@/components/ui/scroll-area.tsx';
import { Input } from '@/components/ui/input.tsx';
import { cn } from '@/lib/utils.ts';
import { AppTooltip } from '@/components/app/app-tooltip.tsx';
import { TProjectSearchItem } from '@/app/products/api/endpoints.ts';
import { useConfirmOnClick } from '@/hooks/use-confirm-on-click.ts';

export function AddToProjectDrawer() {
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [newProjectError, setNewProjectError] = useState('');
  const addToProjectItem = useUiStore((state) => state.addToProjectItem);
  const [search, setSearch] = useState('');
  const { data: projects } = useProjectSearchQuery({ q: search, product_id: addToProjectItem?.productId });
  const createProject = useProjectCreateMutation();
  const addToProject = useAddToProjectMutation();
  const removeFromProject = useRemoveFromProjectMutation();
  useEffect(() => {
    if (addToProjectItem) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [addToProjectItem]);

  const onOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setAddToProjectItem(null);
    }
  };

  const onCreateProject = () => {
    setNewProjectError('');
    if (projectName.trim() === '') {
      setNewProjectError('Project name is required');
      return;
    }
    createProject
      .mutateAsync({
        name: projectName.trim(),
        productId: addToProjectItem?.productId,
        colorId: addToProjectItem?.colorId,
      })
      .then(() => {
        setOpen(false);
        setAddToProjectItem(null);
      });
  };

  const onAddToProject = (project: TProjectSearchItem) => {
    if (project.has_product) {
      return;
    }
    addToProject
      .mutateAsync({
        projectId: project.id,
        productId: addToProjectItem?.productId,
        colorId: addToProjectItem?.colorId,
      })
      .then(() => {
        setOpen(false);
        setAddToProjectItem(null);
      });
  };

  const confirmRemove = useConfirmOnClick<TProjectSearchItem>({
    title: 'Remove Product from Project',
    onShow: (p) => {
      return {
        message: `Are you sure you want to remove product ${addToProjectItem?.name} from the project "${p.name}"?`,
      };
    },
    onConfirm: (p) => {
      removeFromProject
        .mutateAsync({
          projectId: p.id,
          productId: addToProjectItem?.productId,
          colorId: addToProjectItem?.colorId,
        })
        .then(() => {
          setOpen(false);
          setAddToProjectItem(null);
        });
    },
  });

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerClose asChild>
          <Button variant="ghost" className="hover:bg-transparent absolute right-0 top-0 w-12">
            <XIcon />
          </Button>
        </DrawerClose>
        <div className="max-w-xl mx-auto w-full">
          <DrawerHeader className="text-center py-2">
            <DrawerTitle className="font-normal">Add {addToProjectItem?.name} To Project</DrawerTitle>
            <DrawerDescription className="sr-only">Add {addToProjectItem?.name} To Project</DrawerDescription>
          </DrawerHeader>
          {!showCreate ? ( // Add to Existing Project
            <div>
              <ProjectSearchInput onSearch={setSearch} />
              <ScrollArea className="h-48" viewportClassName="max-h-96 pr-2 my-2">
                <div className="flex flex-col gap-2 max-h-96">
                  {projects?.map((project) => (
                    <div
                      key={project.id}
                      className={cn(
                        'flex items-center gap-3 px-2 py-1 border border-gray-200 rounded hover:bg-gray-50',
                        project.has_product ? 'bg-gray-100' : 'cursor-pointer',
                      )}
                      onClick={() => onAddToProject(project)}
                    >
                      {project.has_product ? (
                        <CheckIcon className="text-green-700" size={15} />
                      ) : (
                        <PlusIcon size={15} />
                      )}
                      <span className="font-normal text-base">{project.name}</span>
                      {project.has_product && (
                        <AppTooltip tooltip="Remove from this project">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="size-5"
                            onClick={() => confirmRemove(project)}
                          >
                            <XIcon />
                          </Button>
                        </AppTooltip>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <DrawerFooter className="px-0">
                <Button onClick={() => setShowCreate(true)}>Create New</Button>
              </DrawerFooter>
            </div>
          ) : (
            // Create New Project
            <div>
              <div>
                <Input
                  placeholder="Project Name..."
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  autoFocus={true}
                />
                {newProjectError && <p className="text-sm text-red-600 mt-1">{newProjectError}</p>}
              </div>
              <DrawerFooter className="px-0">
                <Button onClick={onCreateProject}>Create Project</Button>
                <Button variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
              </DrawerFooter>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
